import { type CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, inject, type OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxFloatUiModule, NgxFloatUiPlacements, NgxFloatUiTriggers } from 'ngx-float-ui';

import { SanitizeSrcPipe } from '@shared/pipes/sanitize-src.pipe';

import {
  type IServiceOfferingConfiguration,
  SERVICE_LOGICAL_STATUS,
  type ServiceLogicalStatus,
} from '../../../models/services.model';
import { ServiceLogicalStatusPipe } from '../../../pipes/service-logical-status.pipe';
import { ServicesManagementService } from '../../../services/services-configuration.service';

@UntilDestroy()
@Component({
  selector: 'ume-services-page',
  templateUrl: './services-page.component.html',
  styleUrls: ['./services-page.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    DragDropModule,
    RouterLink,
    ScrollingModule,
    NgxFloatUiModule,
    ServiceLogicalStatusPipe,
    SanitizeSrcPipe,
  ],
})
export class ServicesPageComponent implements OnInit {
  readonly popTriggers = NgxFloatUiTriggers;
  readonly popPlacements = NgxFloatUiPlacements;
  readonly serviceLogicalStatus = SERVICE_LOGICAL_STATUS;

  private route: ActivatedRoute = inject(ActivatedRoute);
  private servicesManagementService: ServicesManagementService = inject(ServicesManagementService);
  private snackBar: MatSnackBar = inject(MatSnackBar);

  public services: IServiceOfferingConfiguration[] = [];
  public currentFilter: ServiceLogicalStatus | null = null;

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe((data) => {
      this.services = data.services;
    });
  }

  public drop(event: CdkDragDrop<string[]>): void {
    const services: IServiceOfferingConfiguration[] = [...this.services];
    moveItemInArray(services, event.previousIndex, event.currentIndex);
    services.forEach((service, index) => {
      service.index = index + 1;
    });
    this.services = services;
    this.servicesReorder(services);
  }

  public servicesReorder(services: IServiceOfferingConfiguration[]): void {
    this.servicesManagementService
      .reorder(services)
      .pipe(untilDestroyed(this))
      .subscribe(() => this.snackBar.open('Reordered', '', { duration: 1500 }));
  }

  public quickFilter(status: ServiceLogicalStatus | null): void {
    this.currentFilter = status;

    this.servicesManagementService
      .getServices(status)
      .pipe(untilDestroyed(this))
      .subscribe((services) => {
        this.services = services;
      });
  }
}
